import { z } from 'zod';
import { useCallback, useMemo } from 'react';
import { useApi, fetchApiRef } from '@backstage/frontend-plugin-api';
import {
  DEFINITION_SOURCES,
  type PulseFactCollectorInfo,
  type PulseIntegrationListItem,
} from '@internal/backstage-plugin-pulse-common';
import {
  fetchCachedValue,
  PULSE_CACHE_KEYS,
  PULSE_QUERY_CACHE_POLICIES,
  useCachedQuery,
} from './cache';

export type PulseIntegrationItem = {
  id: string;
  integrationId: string;
  title?: string;
  description?: string;
  factCollectors: PulseFactCollectorInfo[];
};

const integrationSchema = z.object({
  integrationId: z.string(),
  title: z.string().optional(),
  description: z.string().optional(),
});

const integrationsResponseSchema = z.object({
  integrations: z.array(integrationSchema).default([]),
});

const factCollectorSchema = z.object({
  integrationId: z.string(),
  factCollectorId: z.string(),
  ownerEntityRef: z.string().optional(),
  source: z.enum(DEFINITION_SOURCES).optional(),
  schedule: z.record(z.string(), z.unknown()).default({}),
  collectorConfig: z.record(z.string(), z.unknown()).default({}),
});

const factCollectorsResponseSchema = z.object({
  factCollectors: z.array(factCollectorSchema).default([]),
});

const fetchIntegrationFactCollectors = async (
  fetch: typeof globalThis.fetch,
  integrationId: string,
): Promise<PulseFactCollectorInfo[]> => {
  const factCollectorsResponse = await fetch(
    `plugin://pulse/integrations/${integrationId}/fact-collectors`,
  );

  if (!factCollectorsResponse.ok) {
    throw new Error(
      `Failed to fetch: ${factCollectorsResponse.status} ${factCollectorsResponse.statusText}`,
    );
  }

  const factCollectorsData = await factCollectorsResponse.json();
  return factCollectorsResponseSchema.parse(factCollectorsData).factCollectors;
};

const fetchIntegrations = async (
  fetch: typeof globalThis.fetch,
): Promise<PulseIntegrationListItem[]> => {
  const integrationsResponse = await fetch(`plugin://pulse/integrations`);

  if (!integrationsResponse.ok) {
    throw new Error(
      `Failed to fetch: ${integrationsResponse.status} ${integrationsResponse.statusText}`,
    );
  }

  const integrationsData = await integrationsResponse.json();
  const parsedIntegrations = integrationsResponseSchema.parse(integrationsData);

  return parsedIntegrations.integrations;
};

export const usePulseFactCollectors = (integrationId?: string) => {
  const { fetch } = useApi(fetchApiRef);
  const fetchIntegrationList = useCallback(
    () => fetchIntegrations(fetch),
    [fetch],
  );
  const integrationsQuery = useCachedQuery({
    cacheKey: PULSE_CACHE_KEYS.integrations,
    fetcher: fetchIntegrationList,
    ...PULSE_QUERY_CACHE_POLICIES.definitions,
  });
  const integration = integrationsQuery.value?.find(
    configuredIntegration =>
      configuredIntegration.integrationId === integrationId,
  );
  const integrationIds = useMemo(
    () =>
      (integrationsQuery.value ?? []).map(
        configuredIntegration => configuredIntegration.integrationId,
      ),
    [integrationsQuery.value],
  );
  const integrationIdsKey = useMemo(
    () => [...integrationIds].sort((a, b) => a.localeCompare(b)).join(','),
    [integrationIds],
  );
  const fetchCollectors = useCallback(async () => {
    if (integrationId) {
      return fetchIntegrationFactCollectors(fetch, integrationId);
    }

    const collectorsByIntegration = await Promise.all(
      integrationIds.map(configuredIntegrationId =>
        fetchCachedValue({
          cacheKey: PULSE_CACHE_KEYS.factCollectors(configuredIntegrationId),
          fetcher: () =>
            fetchIntegrationFactCollectors(fetch, configuredIntegrationId),
          ...PULSE_QUERY_CACHE_POLICIES.definitions,
        }),
      ),
    );

    return collectorsByIntegration.flat();
  }, [fetch, integrationId, integrationIds]);
  const collectorsQuery = useCachedQuery({
    cacheKey: integrationId
      ? PULSE_CACHE_KEYS.factCollectors(integrationId)
      : PULSE_CACHE_KEYS.allFactCollectors(integrationIdsKey),
    fetcher: fetchCollectors,
    ...PULSE_QUERY_CACHE_POLICIES.definitions,
    enabled: Boolean(integrationId) || integrationsQuery.value !== undefined,
  });

  const value = useMemo<PulseIntegrationItem[] | undefined>(() => {
    if (!integrationsQuery.value) {
      return undefined;
    }
    if (!collectorsQuery.value) {
      return undefined;
    }
    const factCollectors = collectorsQuery.value;
    if (!integrationId) {
      return integrationsQuery.value.map(configuredIntegration => ({
        id: configuredIntegration.integrationId,
        ...configuredIntegration,
        factCollectors: factCollectors.filter(
          collector =>
            collector.integrationId === configuredIntegration.integrationId,
        ),
      }));
    }
    return [
      {
        id: integrationId,
        integrationId,
        title: integration?.title,
        description: integration?.description,
        factCollectors,
      },
    ];
  }, [
    collectorsQuery.value,
    integration,
    integrationId,
    integrationsQuery.value,
  ]);

  return {
    loading: integrationsQuery.loading || collectorsQuery.loading,
    refreshing: integrationsQuery.refreshing || collectorsQuery.refreshing,
    value,
    integration,
    error: integrationsQuery.error ?? collectorsQuery.error,
    retry: async () => {
      await integrationsQuery.retry();
      await collectorsQuery.retry();
    },
  };
};
